/*
 * Storefront search preprocessor.
 *
 * Shopify receives a single `q` value, so normalisation has to happen before
 * the form leaves the browser.  This deliberately does not add a second query
 * term: Shopify treats space-separated terms as AND, which would turn a
 * spelling fallback into a false negative.
 */
(() => {
  'use strict';

  const dash = /[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFF0D]/g;
  const codeOnly = /^[A-Za-z0-9/_-]+$/;

  const toKatakana = (value) => Array.from(value, (character) => {
    const code = character.codePointAt(0);
    return code >= 0x3041 && code <= 0x3096
      ? String.fromCodePoint(code + 0x60)
      : character;
  }).join('');

  const splitCompactCode = (value) => {
    // OP05119 -> OP05-119, FB10006 -> FB10-006, SV4A349 -> SV4A-349.
    // Existing separators are preserved below; the inserted hyphen only gives
    // a compact model number the same native-search tokens as its usual form.
    let match = value.match(/^([A-Z]{1,5}\d{1,2}[A-Z]?)(\d{2,4}[A-Z]?)$/);
    if (match) return `${match[1]}-${match[2]}`;
    // HG1CP6 -> HG1 CP6 and similar set-code + rarity-number forms.
    match = value.match(/^([A-Z]{1,5}\d{1,2})([A-Z]{1,4}\d{1,4})$/);
    return match ? `${match[1]}-${match[2]}` : value;
  };

  const normaliseSeparatorCode = (value) => value.replace(/^([A-Z]+)-(?=\d)/, '$1');

  const normaliseQuery = (value) => {
    const nfkc = (value || '').normalize('NFKC').trim().replace(/\s+/g, ' ');
    return toKatakana(nfkc).split(' ').map((term) => {
      const separated = normaliseSeparatorCode(term.replace(dash, '-').toUpperCase());
      if (!codeOnly.test(separated)) return term;
      const missingHyphenBeforeSuffix = separated.match(/^([A-Z]{1,5}\d{1,2}[A-Z]?)(\d{2,4})([\/_-].+)$/);
      if (missingHyphenBeforeSuffix) {
        return `${missingHyphenBeforeSuffix[1]}-${missingHyphenBeforeSuffix[2]}${missingHyphenBeforeSuffix[3]}`;
      }
      if (/[\/_-]/.test(separated)) return separated;
      const compact = separated.replace(/[\/_-]/g, '');
      return splitCompactCode(compact);
    }).join(' ').replace(/\s+/g, ' ').trim();
  };

  // Kept as a small exported helper for the normalisation contract test. The
  // browser implementation lives in predictive-search.js; this mirrors its
  // alternate search-view query without taking ownership of presentation.
  const predictiveUrl = (endpoint, query) => {
    const url = new URL(endpoint, 'https://theme.invalid');
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('view', 'suggest');
    return `${url.pathname}${url.search}`;
  };

  const configureContextFallback = () => {
    const fallback = document.querySelector('[data-search-context-fallback]');
    if (!fallback) return;
    const query = normaliseQuery(fallback.dataset.searchQuery || '').toLocaleLowerCase('ja-JP');
    if (!query) return;
    const matchingGenres = Array.from(fallback.querySelectorAll('[data-search-genre-link]')).filter((link) => {
      const terms = (link.dataset.searchGenreTerms || '').toLocaleLowerCase('ja-JP').split('|');
      return terms.some((term) => term.length >= 3 && query.includes(term));
    });
    if (!matchingGenres.length) return;
    fallback.hidden = false;
    matchingGenres.forEach((link) => { link.hidden = false; });
  };

  const bindPackMenu = (menu) => {
    if (!(menu instanceof HTMLElement) || menu.dataset.packMenuReady === 'true') return;
    const input = menu.querySelector('input[type="search"]');
    const empty = menu.querySelector('.pack-menu__empty');
    const groups = Array.from(menu.querySelectorAll('.pack-menu__series'));
    const directItems = groups.length ? [] : Array.from(menu.querySelectorAll('.spine, .pack-tile'));
    if (!(input instanceof HTMLInputElement) || !(empty instanceof HTMLElement)) return;
    if (!groups.length && !directItems.length) return;

    menu.dataset.packMenuReady = 'true';
    groups.forEach((group) => {
      group.dataset.packMenuOpen = group.open ? 'true' : 'false';
    });

    const normalisePackTerm = (value) => (value || '').normalize('NFKC').toLocaleLowerCase('ja-JP');
    const filterPacks = () => {
      const query = normalisePackTerm(input.value.trim());
      let found = false;

      if (groups.length) {
        groups.forEach((group) => {
          const summary = group.querySelector(':scope > summary');
          const groupMatch = query !== '' && normalisePackTerm(summary ? summary.textContent : '').includes(query);
          const items = Array.from(group.querySelectorAll('.spine, .pack-tile'));
          let groupFound = groupMatch;

          items.forEach((item) => {
            const itemMatch = query === '' || groupMatch || normalisePackTerm(item.textContent).includes(query);
            item.hidden = !itemMatch;
            if (itemMatch && query !== '') groupFound = true;
          });

          group.hidden = query !== '' && !groupFound;
          if (query !== '') {
            group.open = groupFound;
            if (groupFound) found = true;
          } else {
            group.open = group.dataset.packMenuOpen === 'true';
          }
        });
      } else {
        directItems.forEach((item) => {
          const itemMatch = query === '' || normalisePackTerm(item.textContent).includes(query);
          item.hidden = !itemMatch;
          if (itemMatch && query !== '') found = true;
        });
      }

      empty.hidden = query === '' || found;
    };

    input.addEventListener('input', filterPacks);
  };

  const bindPackMenus = (scope) => {
    scope.querySelectorAll('[data-pack-menu]').forEach(bindPackMenu);
  };

  const api = { normaliseQuery, predictiveUrl };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-search-normalize-form')) return;
    const input = form.querySelector('[data-search-normalize-input]');
    if (input instanceof HTMLInputElement) input.value = normaliseQuery(input.value);
  });

  window.TorecaSearchNormalise = normaliseQuery;
  configureContextFallback();
  bindPackMenus(document);
  document.addEventListener('shopify:section:load', (event) => {
    bindPackMenus(event.target);
  });
})();
